// Company stores (S3, 2026-10-12) — /join/:slug, public.
//
// "Anyone with an @company email can join": a work email (and a name) →
// POST /portal/store/join/:slug. The API answers the same whatever happens
// (joined, wrong domain, already in, store closed), so this page always says
// the same thing back — it never tells a stranger whether an email is known.
// The title uses the store's real name (GET /portal/store/join/:slug/info,
// name only), falling back to a name made from the link.
import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ApiError } from "@/lib/api";
import { fetchJoinInfo, joinStore, nameFromSlug } from "@/lib/portal-store";
import { StoreNotice, StorePublicShell } from "@/components/StorePublicShell";

const INPUT =
  "w-full rounded-md border border-cream-400 bg-white px-4 py-3 text-brown-500 placeholder:text-[#8a8a85] shadow-sm focus:border-peach-400 focus:outline-none focus:ring-0";

export function StoreJoinPage() {
  const { slug = "" } = useParams<{ slug: string }>();
  const info = useQuery({ queryKey: ["store-join-info", slug], queryFn: () => fetchJoinInfo(slug), retry: false, staleTime: 300_000 });
  const storeName = info.data?.storeName || nameFromSlug(slug) || "this store";
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await joinStore(slug, email.trim(), name);
      setSent(true);
    } catch (err) {
      // 400 / 404 say nothing a stranger may learn from: same answer as success.
      if (err instanceof ApiError && (err.status === 400 || err.status === 404)) setSent(true);
      else if (err instanceof ApiError && err.status === 429) setError("Too many tries. Please wait a minute and try again.");
      else setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <StorePublicShell narrow>
        <StoreNotice title="Check your email">
          <p>If that's a company email for {storeName}, we've sent you a link to set your password.</p>
          <p>Nothing after a few minutes? Ask your store admin to add you.</p>
          <p>
            Already set one?{" "}
            <Link to="/login" state={{ from: "/store" }} className="font-semibold text-ink underline-offset-2 hover:underline">
              Sign in
            </Link>
          </p>
        </StoreNotice>
      </StorePublicShell>
    );
  }

  return (
    <StorePublicShell narrow>
      <h1 className="mb-2 font-display text-4xl font-bold tracking-tight">Join {storeName}</h1>
      <p className="mb-10 text-brown-400">
        Order company gear with your logo on it. Enter your work email and we'll send you a link to set your password.
      </p>
      <form onSubmit={onSubmit} className="space-y-5">
        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-brown-500">Work email</span>
          <input
            type="email"
            required
            autoFocus
            maxLength={254}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={INPUT}
            autoComplete="email"
          />
        </label>
        <label className="block">
          <span className="mb-2 block text-sm font-semibold text-brown-500">Your name <span className="font-normal text-brown-300">(optional)</span></span>
          <input
            type="text"
            maxLength={200}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={INPUT}
            autoComplete="name"
          />
        </label>
        {error && (
          <div role="alert" className="rounded-md border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-500">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-md bg-ink px-4 py-3 font-semibold text-white shadow-sm transition-colors hover:bg-ink-700 disabled:opacity-50"
        >
          {submitting ? "Sending…" : "Send me a link"}
        </button>
      </form>
      <p className="mt-8 text-sm text-brown-400">
        Already have a password?{" "}
        <Link to="/login" state={{ from: "/store" }} className="font-semibold text-ink underline-offset-2 hover:underline">
          Sign in
        </Link>
      </p>
    </StorePublicShell>
  );
}
